import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, Hammer, Users, Star, Car, Cpu, Shirt, Home as HomeIcon, Wrench, Sparkles } from "lucide-react";

const CAT_COLOR = {
  automobile: "#C05A3C",
  crafts: "#D9923B",
  electronics: "#2A3D36",
  fashion: "#B84A28",
  architecture: "#4A5D23",
  other: "#1A2024",
};

// What people can showcase — drives the invite grid when there are no submissions yet.
const TALENT_CATS = [
  { Icon: Car, label: "Automobile Engineering", color: "#C05A3C" },
  { Icon: Hammer, label: "Crafts & Artisan", color: "#D9923B" },
  { Icon: Cpu, label: "Electronics & Robotics", color: "#2A3D36" },
  { Icon: Shirt, label: "Fashion Design", color: "#B84A28" },
  { Icon: HomeIcon, label: "Architecture & Furniture", color: "#4A5D23" },
  { Icon: Wrench, label: "Anything you build", color: "#1A2024" },
];

// Homepage Talent & Makers spotlight. Always rendered: shows a live rail once
// there are 3+ showcases, otherwise an inviting "showcase your talent" CTA so
// the section is always visible and recruiting new makers.
const FeaturedMakers = () => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/makers/showcases?sort=featured&limit=10")
      .then((r) => setItems(r.data?.items || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const hasRail = loaded && items.length >= 3;

  return (
    <section className="bg-[#FBF6EE] border-y border-black/5" data-testid="featured-makers-section">
      <div className="container-narrow py-16 sm:py-20">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="label-overline text-[#C05A3C] mb-2 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> Talent &amp; Makers Exhibition
            </div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight">
              Got a talent? Showcase it to the world.
            </h2>
            <p className="text-muted-foreground mt-2 max-w-xl font-sans-ui leading-relaxed">
              From custom cars and robotics to fashion, furniture and fine crafts — if you can
              build, make or create it, put it on the exhibition floor and get discovered by
              sponsors and investors across the diaspora.
            </p>
          </div>
          <Link
            to="/makers/new"
            data-testid="featured-makers-showcase-cta"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-white hover:opacity-95 transition-opacity shrink-0 font-semibold text-sm"
            style={{ background: "linear-gradient(135deg, #C05A3C 0%, #D9923B 100%)" }}
          >
            <Sparkles className="w-4 h-4" /> Showcase your talent
          </Link>
        </div>

        {hasRail ? (
          <div
            className="flex gap-4 overflow-x-auto pb-3 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x snap-mandatory"
            data-testid="featured-makers-rail"
          >
            {items.map((s) => {
              const cover = s.image_urls?.[0];
              const color = CAT_COLOR[s.category] || CAT_COLOR.other;
              return (
                <Link
                  key={s.showcase_id}
                  to={`/makers/${s.showcase_id}`}
                  data-testid={`featured-maker-${s.showcase_id}`}
                  className="snap-start flex-shrink-0 w-[260px] sm:w-[280px] group bg-white rounded-2xl border border-black/10 overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all"
                >
                  <div className="relative aspect-[4/3] bg-[#F3EFE9] overflow-hidden">
                    {cover ? (
                      <img
                        src={cover}
                        alt={s.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#C05A3C]/30">
                        <Hammer className="w-10 h-10" />
                      </div>
                    )}
                    {s.is_featured && (
                      <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#D9923B] text-white text-[10px] font-bold uppercase tracking-wider">
                        <Star className="w-2.5 h-2.5" /> Featured
                      </div>
                    )}
                    <div
                      className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-white capitalize"
                      style={{ background: color }}
                    >
                      {s.category}
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="font-display text-lg leading-snug line-clamp-2 group-hover:text-[#C05A3C] transition-colors">
                      {s.title}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 truncate">
                      {s.creator_name} · {s.country}
                    </div>
                    <div className="flex items-center gap-1 mt-3 text-xs text-muted-foreground">
                      <Users className="w-3.5 h-3.5" /> {s.sponsor_count} interested
                    </div>
                  </div>
                </Link>
              );
            })}

            {/* Tail CTA card */}
            <Link
              to="/makers"
              data-testid="featured-makers-tail-cta"
              className="snap-start flex-shrink-0 w-[200px] rounded-2xl border-2 border-dashed border-[#C05A3C]/40 flex flex-col items-center justify-center text-center p-6 hover:border-[#C05A3C] hover:bg-[#C05A3C]/5 transition-colors"
            >
              <Hammer className="w-6 h-6 text-[#C05A3C] mb-2" />
              <div className="font-semibold text-[#1A2024]">See every maker</div>
              <div className="text-xs text-muted-foreground mt-1">Browse by craft &amp; country →</div>
            </Link>
          </div>
        ) : (
          // Invite state — no (or few) showcases yet. Always visible & recruiting.
          <div data-testid="featured-makers-invite">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-6">
              {TALENT_CATS.map((c) => (
                <div
                  key={c.label}
                  className="bg-white rounded-2xl border border-black/10 p-4 flex flex-col items-center text-center hover:border-[#C05A3C] hover:shadow-md transition-all"
                  data-testid={`talent-cat-${c.label.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-2"
                    style={{ background: `${c.color}15` }}
                  >
                    <c.Icon className="w-5 h-5" style={{ color: c.color }} />
                  </div>
                  <div className="text-xs font-semibold leading-snug text-[#1A2024]">{c.label}</div>
                </div>
              ))}
            </div>
            <div className="rounded-2xl border-2 border-dashed border-[#C05A3C]/40 bg-white p-8 sm:p-10 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#C05A3C]/10 mb-3">
                <Sparkles className="w-6 h-6 text-[#C05A3C]" />
              </div>
              <h3 className="font-display text-2xl sm:text-3xl tracking-tight">Be the first on the exhibition floor.</h3>
              <p className="text-muted-foreground mt-2 max-w-lg mx-auto font-sans-ui">
                Whether you weld, weave, sew, code or carve — share your work, build a following,
                and connect with people ready to back African talent.
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/makers/new"
                  data-testid="featured-makers-invite-cta"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-white font-semibold text-sm hover:opacity-95 transition-opacity"
                  style={{ background: "linear-gradient(135deg, #C05A3C 0%, #D9923B 100%)" }}
                >
                  <Sparkles className="w-4 h-4" /> Showcase your talent
                </Link>
                <Link
                  to="/makers"
                  data-testid="featured-makers-invite-explore"
                  className="inline-flex items-center gap-1 text-sm font-medium text-[#1A2024] hover:text-[#C05A3C] transition-colors"
                >
                  Explore the exhibition <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedMakers;
